'use client';

import { Button } from '@/components/button';
import HeadlineField from '@/components/customFields/HeadlineField';
import { HeadlineTag } from '@/components/customFields/types';
import FaqFeedback from '@/components/faqFeedback/FaqFeedback';
import Pill from '@/components/pill/Pill';
import { RichText } from '@/components/richText/RichText';
import { SvgIcon } from '@/components/svgIcon';
import {
  SwdAccordionHeader,
  SwdAccordionItem,
  SwdAccordionPanel,
  SwdAccordionRoot,
  SwdAccordionTrigger,
} from '@/components/swdAccordion/SwdAccordion';
import { useTopicsAndTags } from '@/hooks/useTopicsAndTags';
import ArrowForwardIcon from '@/public/icons/pfeil-rechts-line.svg';
import type { PageFaqStoryblok } from '@/types/component-types-sb';
import type { ISbStoryData } from '@storyblok/react/rsc';
import { useTranslation } from 'react-i18next';

import classes from './FaqAccordion.module.scss';

export type FaqAccordionProps = {
  headline: React.JSX.Element | string;
  faqFeedbackText: string | undefined;
  faqItems: ISbStoryData<PageFaqStoryblok>[];
  slug?: string;
  withTags?: boolean;
  storyId: number;
};

const PANEL_VALUE = 'faq';

export const FaqAccordion: React.FC<FaqAccordionProps> = ({
  headline,
  faqItems,
  withTags,
  slug,
  storyId,
  faqFeedbackText,
}) => {
  const { t } = useTranslation();

  let path = slug ?? '';

  // rm the first and last slash from the string
  path = path.replace(/^\/|\/$/g, '');

  // only use the first 3 levels for the categories
  path = path.split('/').slice(0, 3).join('/');

  const { tags, selectedTag, setSelectedTag, expandedIndex, setExpandedIndex, filteredTopics, loadMore, hasMore } =
    useTopicsAndTags(faqItems);

  return (
    <div role="region">
      <div className={classes.topContent}>
        <HeadlineField
          className={classes.title}
          tag={HeadlineTag.H2}
          style={HeadlineTag.H3}
          data-testid="faq-related-content-heading"
        >
          {headline}
        </HeadlineField>

        {withTags && tags.length > 0 && (
          <div className={classes.tags}>
            <Pill color={'green'} selected={!selectedTag} onClick={() => setSelectedTag('')}>
              {t('Alle Hilfetexte')}
            </Pill>
            {tags.map((tag, key) => {
              return (
                <Pill key={key} color={'green'} selected={selectedTag === tag} onClick={() => setSelectedTag(tag)}>
                  {tag}
                </Pill>
              );
            })}
          </div>
        )}
      </div>

      <div className={classes.accordionWrapper} role="region" aria-label={t('Abschnitt Hilfetexte')}>
        {filteredTopics
          .filter((topic) => topic.id !== storyId)
          .map((topic, k) => {
            const accordionId = `faq-accordion-${k}`;
            const headerId = `faq-header-${k}`;
            const contentId = `faq-content-${k}`;

            return (
              <SwdAccordionRoot
                data-testid="faq-accordion"
                key={k}
                id={accordionId}
                variant="standard"
                value={expandedIndex === k ? [PANEL_VALUE] : []}
                onValueChange={(next) => {
                  if (next.includes(PANEL_VALUE)) {
                    setExpandedIndex(k);
                  } else {
                    setExpandedIndex(-1);
                  }
                }}
              >
                <SwdAccordionItem value={PANEL_VALUE}>
                  <SwdAccordionHeader>
                    <SwdAccordionTrigger id={headerId} aria-controls={contentId}>
                      {topic.content.title}
                    </SwdAccordionTrigger>
                  </SwdAccordionHeader>
                  <SwdAccordionPanel id={contentId}>
                    <RichText text={topic.content.text} />
                    {expandedIndex === k && (
                      <FaqFeedback
                        dimmed
                        storyId={topic.id}
                        storySlug={topic.full_slug}
                        text={faqFeedbackText}
                        className={classes.faqFeedback}
                      />
                    )}
                  </SwdAccordionPanel>
                </SwdAccordionItem>
              </SwdAccordionRoot>
            );
          })}

        {hasMore && (
          <div className={classes.loadMoreContainer}>
            <Button
              data-testid="faq-load-more-button"
              variant="text"
              onClick={() => loadMore()}
              endIcon={<SvgIcon component={ArrowForwardIcon} />}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  loadMore();
                }
              }}
              tabIndex={0}
              aria-label={t('Alle Fragen ansehen')}
            >
              {t('Alle Fragen ansehen')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaqAccordion;
